import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import BatchPlanModal from './BatchPlanModal';
import BankAvailabilitySummary from './BankAvailabilitySummary';
import {
  millProductionAPI,
  millStoreAPI,
  productionPlanAPI,
  ProductionPlan,
  ProductionPlanOrderItem,
  ProductionPlanSource,
} from '../../services/millAPI';
import './Mill.css';

interface StoreAlert {
  id: number;
  alert_type: string;
  alert_type_display: string;
  priority: string;
  priority_display: string;
  status: string;
  title: string;
  message: string;
  created_at: string;
  product_name: string | null;
  current_quantity: number | string | null;
  threshold: number | string | null;
  formula_id: number | null;
  formula_name: string | null;
  restock_quantity: number | null;
}

interface ActiveBatch {
  id: number;
  batch_number: string;
  formula_name: string;
  feed_form: string;
  planned_weight: number;
  status: string;
  status_display: string;
}

// Grouped order structure
interface GroupedOrder {
  order_id: number;
  order_number: string;
  customer_name: string;
  delivery_date: string | null;
  delivery_method?: string;
  total_items: number;
  total_lbs: number;
  items: ProductionPlanOrderItem[];
}

interface DateGroup {
  date: string;
  orders: GroupedOrder[];
  total_orders: number;
  total_items: number;
}

// Helper to get correct package display - calculates from quantity data when backend is unreliable
const getPackageDisplay = (item: ProductionPlanOrderItem): string => {
  const totalLbs = item.total_quantity_lbs || 0;
  const numPackages = item.number_of_packages || 1;
  const lbsPerPackage = numPackages > 0 ? totalLbs / numPackages : totalLbs;

  // Calculate package type from actual data - this is more reliable than stored package_type
  // which may be wrong in the database
  if (lbsPerPackage >= 500) {
    // Super sack territory (500+ lbs per package)
    return `${Math.round(lbsPerPackage)}# Super Sack`;
  } else if (lbsPerPackage >= 35 && lbsPerPackage <= 45) {
    // Standard 40lb bags
    return '40# Bags';
  } else if (lbsPerPackage >= 15 && lbsPerPackage <= 25) {
    // 19lb Tiny Dino bags
    return '19# Bags';
  } else if (item.package_type === 'BYOC') {
    return 'BYOC';
  } else if (item.package_type === 'BULK') {
    return 'Bulk';
  } else if (lbsPerPackage > 0) {
    // Fallback: show calculated weight
    return `${Math.round(lbsPerPackage)}# Pkg`;
  }

  // Last resort: use backend value if available
  if (item.package_type_display && !item.package_type_display.includes('undefined')) {
    return item.package_type_display;
  }

  return item.package_type || 'Unknown';
};

const MillDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [productionPlan, setProductionPlan] = useState<ProductionPlan | null>(null);
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  const [storeAlerts, setStoreAlerts] = useState<StoreAlert[]>([]);
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState<StoreAlert[]>([]);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [alertActionLoading, setAlertActionLoading] = useState<number | null>(null);
  const [feedBankInfo, setFeedBankInfo] = useState<{ alertId: number; available: boolean; total: number; formula: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pullingSource, setPullingSource] = useState<string | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load all data in parallel
      const [planResponse, batchesResponse, alertsResponse] = await Promise.all([
        productionPlanAPI.getPlan(14),
        millProductionAPI.getBatches({ status: 'IN_PRODUCTION' }),
        millStoreAPI.getStoreAlerts().catch(() => ({ data: { alerts: [] } })),
      ]);

      setProductionPlan(planResponse.data);
      setActiveBatches(batchesResponse.data.results || batchesResponse.data || []);
      setStoreAlerts(alertsResponse.data.alerts || []);
      setAcknowledgedAlerts(alertsResponse.data.acknowledged || []);
    } catch (err: any) {
      setError('Failed to load production plan. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  // ── Store Alert Action Handlers ──
  const handleAlertAcknowledge = async (alertId: number) => {
    try {
      setAlertActionLoading(alertId);
      await millStoreAPI.acknowledgeAlert(alertId);
      showSuccess('Alert acknowledged');
      await loadData();
    } catch (err: any) {
      alert('Failed to acknowledge: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const handleAlertDismiss = async (alertId: number) => {
    try {
      setAlertActionLoading(alertId);
      await millStoreAPI.dismissAlert(alertId);
      showSuccess('Alert dismissed for 24 hours');
      await loadData();
    } catch (err: any) {
      alert('Failed to dismiss: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const handleAlertResolve = async (alertId: number) => {
    try {
      setAlertActionLoading(alertId);
      await millStoreAPI.resolveAlert(alertId);
      showSuccess('Alert resolved');
      await loadData();
    } catch (err: any) {
      alert('Failed to resolve: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const handleAlertStartBatch = async (alertId: number) => {
    try {
      setAlertActionLoading(alertId);
      const res = await millStoreAPI.startBatch(alertId);
      showSuccess(`Batch ${res.data.batch_number} created — ${res.data.planned_weight.toLocaleString()} lbs`);
      navigate(`/mill/mixing/${res.data.batch_id}`);
    } catch (err: any) {
      alert('Failed to create batch: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const handleAlertPullFromBank = async (alertId: number) => {
    try {
      setAlertActionLoading(alertId);
      // First check availability
      const res = await millStoreAPI.pullFromBank(alertId);
      if (!res.data.available) {
        alert(res.data.message || 'No feed bank inventory available for this formula');
        return;
      }
      setFeedBankInfo({
        alertId,
        available: true,
        total: res.data.total_available_lbs,
        formula: res.data.formula_name,
      });
    } catch (err: any) {
      alert('Failed to check feed bank: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const handleConfirmPullFromBank = async () => {
    if (!feedBankInfo) return;
    try {
      setAlertActionLoading(feedBankInfo.alertId);
      const res = await millStoreAPI.pullFromBank(feedBankInfo.alertId, {
        confirm: true,
        quantity_lbs: feedBankInfo.total,
      });
      showSuccess(res.data.message);
      setFeedBankInfo(null);
      await loadData();
    } catch (err: any) {
      alert('Failed to allocate: ' + (err.response?.data?.error || err.message));
    } finally {
      setAlertActionLoading(null);
    }
  };

  const isRestockAlert = (alert: StoreAlert) =>
    alert.alert_type === 'RESTOCK_REQUEST';

  const isLowInventoryAlert = (alert: StoreAlert) =>
    ['LOW_INVENTORY', 'BULK_BIN_LOW'].includes(alert.alert_type);

  const handlePullFromBank = async (item: ProductionPlanOrderItem, source: ProductionPlanSource) => {
    if (!source.entries || source.entries.length === 0) {
      alert('No feed bank entries available for this source.');
      return;
    }

    const key = `bank-${item.order_item_id}-${source.entries[0].id}`;
    try {
      setPullingSource(key);
      // Pull from each feed bank entry
      for (const entry of source.entries) {
        await productionPlanAPI.pullFromBank({
          order_item_id: item.order_item_id,
          feed_bank_id: entry.id,
          quantity_lbs: entry.quantity_lbs,
        });
      }
      showSuccess(`Pulled ${source.quantity_lbs.toLocaleString()} lbs from Feed Bank for ${item.formula_name}`);
      await loadData();
    } catch (err: any) {
      alert('Failed to pull from Feed Bank: ' + (err.response?.data?.error || err.message));
    } finally {
      setPullingSource(null);
    }
  };

  const handlePullFromStore = async (item: ProductionPlanOrderItem, source: ProductionPlanSource) => {
    if (!source.source_id) {
      alert('No store inventory item linked to this source.');
      return;
    }

    const key = `store-${item.order_item_id}-${source.source_id}`;
    try {
      setPullingSource(key);
      await productionPlanAPI.pullFromStore({
        order_item_id: item.order_item_id,
        store_inventory_id: source.source_id,
        quantity_bags: source.quantity_bags || Math.ceil(source.quantity_lbs / 40),
      });
      showSuccess(`Store task created: Pull ${source.quantity_bags || ''} bags of ${item.formula_name}`);
      await loadData();
    } catch (err: any) {
      alert('Failed to create store pull task: ' + (err.response?.data?.error || err.message));
    } finally {
      setPullingSource(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateLong = (dateString: string | null) => {
    if (!dateString) return 'No Date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get source badge class
  const getSourceBadgeClass = (sourceType: string) => {
    switch (sourceType) {
      case 'FEED_BANK':
        return 'source-feed-bank';
      case 'STORE_BAGGED':
        return 'source-store';
      case 'BYOC_BIN':
        return 'source-byoc';
      case 'PRODUCTION':
        return 'source-production';
      default:
        return 'source-production';
    }
  };

  // Get source label
  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'FEED_BANK':
        return 'Feed Bank';
      case 'STORE_BAGGED':
        return 'Store Bags';
      case 'BYOC_BIN':
        return 'BYOC Bin';
      case 'PRODUCTION':
        return 'Production';
      default:
        return 'Production';
    }
  };

  // Get item status info
  const getItemStatus = (item: ProductionPlanOrderItem) => {
    const percent = item.percent_fulfilled;

    if (percent >= 100 || item.status === 'FULLY_FULFILLED' || item.status === 'READY') {
      return { label: 'READY', className: 'item-status-ready' };
    }

    if (item.needs_production_lbs > 0 && item.needs_production_lbs >= item.total_quantity_lbs) {
      return { label: 'NEEDS PRODUCTION', className: 'item-status-needs' };
    }

    if (percent > 0 && percent < 100) {
      return { label: `PARTIAL ${Math.round(percent)}%`, className: 'item-status-partial' };
    }

    if (item.status === 'IN_PRODUCTION' || item.status === 'MILLING') {
      return { label: 'IN PROGRESS', className: 'item-status-progress' };
    }

    return { label: 'NEEDS PRODUCTION', className: 'item-status-needs' };
  };

  // Group items by order, then by date
  const groupOrdersByDate = (items: ProductionPlanOrderItem[]): DateGroup[] => {
    // First, group items by date
    const dateGroups: { [date: string]: ProductionPlanOrderItem[] } = {};
    items.forEach(item => {
      const date = item.delivery_date || 'No Date';
      if (!dateGroups[date]) dateGroups[date] = [];
      dateGroups[date].push(item);
    });

    // Then, for each date, group items by order
    const result: DateGroup[] = [];

    Object.entries(dateGroups)
      .sort(([a], [b]) => {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return a.localeCompare(b);
      })
      .forEach(([date, dateItems]) => {
        // Group items by order within this date
        const orderMap: { [orderId: number]: GroupedOrder } = {};

        dateItems.forEach(item => {
          if (!orderMap[item.order_id]) {
            orderMap[item.order_id] = {
              order_id: item.order_id,
              order_number: item.order_number,
              customer_name: item.customer_name,
              delivery_date: item.delivery_date,
              total_items: 0,
              total_lbs: 0,
              items: [],
            };
          }
          orderMap[item.order_id].items.push(item);
          orderMap[item.order_id].total_items++;
          orderMap[item.order_id].total_lbs += item.total_quantity_lbs;
        });

        const orders = Object.values(orderMap).sort((a, b) =>
          a.order_number.localeCompare(b.order_number)
        );

        result.push({
          date,
          orders,
          total_orders: orders.length,
          total_items: dateItems.length,
        });
      });

    return result;
  };

  // Render source badges with pull action buttons for an item
  const renderSources = (item: ProductionPlanOrderItem) => {
    const sources = item.sources;
    if (!sources || sources.length === 0) {
      return <span className="source-badge source-production">Production</span>;
    }

    return (
      <div className="sources-list">
        {sources.map((source, idx) => {
          const isAvailable = source.status === 'AVAILABLE';
          const isFeedBank = source.source_type === 'FEED_BANK';
          const isStore = source.source_type === 'STORE_BAGGED';
          const showPull = isAvailable && (isFeedBank || isStore);

          const pullKey = isFeedBank
            ? `bank-${item.order_item_id}-${source.entries?.[0]?.id || 0}`
            : `store-${item.order_item_id}-${source.source_id || 0}`;
          const isPulling = pullingSource === pullKey;

          return (
            <span key={idx} className="source-with-action">
              <span className={`source-badge ${getSourceBadgeClass(source.source_type)}`}>
                {getSourceLabel(source.source_type)} {source.quantity_lbs.toLocaleString()}
              </span>
              {showPull && (
                <button
                  className="source-pull-btn"
                  onClick={() =>
                    isFeedBank
                      ? handlePullFromBank(item, source)
                      : handlePullFromStore(item, source)
                  }
                  disabled={isPulling}
                  title={isFeedBank ? 'Pull from Feed Bank' : 'Send store pull task'}
                >
                  {isPulling ? '...' : 'Pull'}
                </button>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading production plan...</p>
        </div>
      </div>
    );
  }

  const dateGroups = productionPlan ? groupOrdersByDate(productionPlan.order_items) : [];

  return (
    <div className="mill-page">
      {/* Success Toast */}
      {successMessage && (
        <div className="mill-toast mill-toast-success">
          <span className="toast-icon">&#10003;</span>
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="mill-dashboard-header">
        <div className="header-content">
          <h1>Production Dashboard</h1>
          <p className="header-subtitle">
            Auto-calculated production plan for next {productionPlan?.planning_window.days || 14} days
          </p>
        </div>
        <div className="header-actions">
          <button
            className="mill-btn mill-btn-secondary"
            onClick={() => navigate('/mill/inventory')}
          >
            Raw Inventory
          </button>
          <button
            className="mill-btn mill-btn-secondary"
            onClick={() => navigate('/mill/feed-bank')}
          >
            Feed Bank
          </button>
        </div>
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {/* Summary Stats */}
      {productionPlan && (
        <div className="mill-status-summary">
          <div className="mill-summary-card">
            <span className="mill-summary-count">{productionPlan.summary.total_order_items}</span>
            <span className="mill-summary-label">Order Items</span>
          </div>
          <div className="mill-summary-card success">
            <span className="mill-summary-count">{productionPlan.summary.items_from_inventory}</span>
            <span className="mill-summary-label">From Inventory</span>
          </div>
          <div className="mill-summary-card warning">
            <span className="mill-summary-count">{productionPlan.summary.items_needing_production}</span>
            <span className="mill-summary-label">Need Production</span>
          </div>
          <div className="mill-summary-card info">
            <span className="mill-summary-count">{productionPlan.summary.total_batches_needed}</span>
            <span className="mill-summary-label">Batches Needed</span>
          </div>
          <div className="mill-summary-card">
            <span className="mill-summary-count">{productionPlan.summary.total_lbs_to_produce.toLocaleString()}</span>
            <span className="mill-summary-label">Lbs to Produce</span>
          </div>
        </div>
      )}

      {/* Active Batches */}
      {activeBatches.length > 0 && (
        <div className="mill-section">
          <h2 className="mill-section-title">
            Batches In Production ({activeBatches.length})
          </h2>
          <div className="mill-active-batches-grid">
            {activeBatches.map(batch => (
              <div key={batch.id} className="mill-active-batch-card">
                <div className="batch-header">
                  <span className="batch-number">{batch.batch_number}</span>
                  <span className={`batch-form form-${batch.feed_form.toLowerCase()}`}>
                    {batch.feed_form}
                  </span>
                </div>
                <div className="batch-formula">{batch.formula_name}</div>
                <div className="batch-weight">{batch.planned_weight.toLocaleString()} lbs</div>
                <button
                  className="mill-btn mill-btn-sm mill-btn-success"
                  onClick={() => navigate(`/mill/mixing/${batch.id}`)}
                >
                  Go to Mixing
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store Alerts */}
      {(storeAlerts.length > 0 || acknowledgedAlerts.length > 0) && (
        <div className="mill-section">
          <h2 className="mill-section-title">
            Store Inventory Alerts ({storeAlerts.length})
          </h2>
          <div className="mill-store-alerts-grid">
            {storeAlerts.map((sa) => {
              const isLoading = alertActionLoading === sa.id;
              const isRestock = isRestockAlert(sa);
              const isLowInv = isLowInventoryAlert(sa);
              return (
                <div
                  key={sa.id}
                  className={`mill-store-alert-card priority-${sa.priority.toLowerCase()}`}
                >
                  <div className="mill-store-alert-header">
                    <span className="alert-type">{sa.alert_type_display}</span>
                    <span className={`alert-priority priority-${sa.priority.toLowerCase()}`}>
                      {sa.priority_display}
                    </span>
                  </div>
                  <h3 className="alert-title">{sa.title}</h3>
                  <p className="alert-message">{sa.message}</p>
                  {sa.formula_name && (
                    <p className="alert-formula">Formula: {sa.formula_name}</p>
                  )}
                  <div className="alert-actions">
                    {isRestock && (
                      <>
                        <button
                          className="alert-btn alert-btn-primary"
                          onClick={() => handleAlertStartBatch(sa.id)}
                          disabled={isLoading}
                        >
                          {isLoading ? '...' : 'Start Batch'}
                        </button>
                        <button
                          className="alert-btn alert-btn-secondary"
                          onClick={() => handleAlertPullFromBank(sa.id)}
                          disabled={isLoading}
                        >
                          {isLoading ? '...' : 'Pull from Feed Bank'}
                        </button>
                        <button
                          className="alert-btn alert-btn-ghost"
                          onClick={() => handleAlertAcknowledge(sa.id)}
                          disabled={isLoading}
                        >
                          Acknowledge
                        </button>
                      </>
                    )}
                    {isLowInv && (
                      <>
                        <button
                          className="alert-btn alert-btn-primary"
                          onClick={() => handleAlertStartBatch(sa.id)}
                          disabled={isLoading || !sa.formula_id}
                          title={!sa.formula_id ? 'No formula linked' : 'Schedule production for this formula'}
                        >
                          {isLoading ? '...' : 'Schedule Production'}
                        </button>
                        <button
                          className="alert-btn alert-btn-ghost"
                          onClick={() => handleAlertDismiss(sa.id)}
                          disabled={isLoading}
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                    {!isRestock && !isLowInv && (
                      <button
                        className="alert-btn alert-btn-ghost"
                        onClick={() => handleAlertResolve(sa.id)}
                        disabled={isLoading}
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* In Progress (Acknowledged) */}
          {acknowledgedAlerts.length > 0 && (
            <div className="alert-acknowledged-section">
              <button
                className="alert-acknowledged-toggle"
                onClick={() => setShowAcknowledged(!showAcknowledged)}
              >
                In Progress ({acknowledgedAlerts.length})
                <span className={`toggle-arrow ${showAcknowledged ? 'open' : ''}`}>&#9662;</span>
              </button>
              {showAcknowledged && (
                <div className="mill-store-alerts-grid acknowledged-grid">
                  {acknowledgedAlerts.map((sa) => {
                    const isLoading = alertActionLoading === sa.id;
                    return (
                      <div
                        key={sa.id}
                        className="mill-store-alert-card alert-acknowledged"
                      >
                        <div className="mill-store-alert-header">
                          <span className="alert-type">{sa.alert_type_display}</span>
                          <span className="alert-status-badge">In Progress</span>
                        </div>
                        <h3 className="alert-title">{sa.title}</h3>
                        <p className="alert-message">{sa.message}</p>
                        <div className="alert-actions">
                          <button
                            className="alert-btn alert-btn-success"
                            onClick={() => handleAlertResolve(sa.id)}
                            disabled={isLoading}
                          >
                            {isLoading ? '...' : 'Mark Resolved'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Feed Bank Pull Confirmation Modal */}
      {feedBankInfo && (
        <div className="alert-modal-overlay" onClick={() => setFeedBankInfo(null)}>
          <div className="alert-modal" onClick={e => e.stopPropagation()}>
            <h3>Pull from Feed Bank</h3>
            <p>
              <strong>{feedBankInfo.total.toLocaleString()} lbs</strong> of{' '}
              <strong>{feedBankInfo.formula}</strong> available in Feed Bank.
            </p>
            <p>Allocate this feed for store restock?</p>
            <div className="alert-modal-actions">
              <button
                className="alert-btn alert-btn-ghost"
                onClick={() => setFeedBankInfo(null)}
              >
                Cancel
              </button>
              <button
                className="alert-btn alert-btn-primary"
                onClick={handleConfirmPullFromBank}
                disabled={alertActionLoading !== null}
              >
                {alertActionLoading !== null ? 'Allocating...' : `Allocate ${feedBankInfo.total.toLocaleString()} lbs`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Planned Batches (auto-created) */}
      {productionPlan && productionPlan.planned_batches?.length > 0 && (
        <div className="mill-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="mill-section-title" style={{ margin: 0 }}>
              Planned Batches ({productionPlan.planned_batches.length})
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BankAvailabilitySummary onOpen={() => setShowPlanModal(true)} />
              <button
                className="mill-btn mill-btn-sm"
                onClick={() => setShowPlanModal(true)}
                title="Plan & prioritize batches by ship-date range"
              >
                Plan
              </button>
            </div>
          </div>
          <div className="mill-suggested-batches">
            <MobileScrollTable>
            <table className="mill-table">
              <thead>
                <tr>
                  <th>Batch #</th>
                  <th>Formula</th>
                  <th>Form</th>
                  <th>Size</th>
                  <th>Planned Date</th>
                  <th>Orders</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {productionPlan.planned_batches.map(batch => (
                  <tr key={batch.id}>
                    <td><strong>{batch.batch_number}</strong></td>
                    <td className="formula-cell">
                      <strong>{batch.formula_name}</strong>
                    </td>
                    <td>
                      <span className={`feed-form-badge form-${batch.feed_form.toLowerCase()}`}>
                        {batch.feed_form}
                      </span>
                    </td>
                    <td className="weight-cell">
                      <strong>{batch.planned_weight.toLocaleString()}</strong> lbs
                    </td>
                    <td>{formatDate(batch.planned_date)}</td>
                    <td>
                      <span className="orders-preview">
                        {batch.orders.slice(0, 2).map(o => o.order_number).join(', ')}
                        {batch.orders.length > 2 && ` +${batch.orders.length - 2}`}
                      </span>
                    </td>
                    <td>
                      <button
                        className="mill-btn mill-btn-sm mill-btn-success"
                        onClick={() => navigate(`/mill/mixing/${batch.id}`)}
                      >
                        Start Mixing
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </MobileScrollTable>
          </div>
        </div>
      )}

      {/* Recent Auto-Batch Activity */}
      {productionPlan && productionPlan.recent_activity?.length > 0 && (
        <div className="mill-section">
          <h2 className="mill-section-title">Recent Auto-Batch Activity</h2>
          <div className="mill-activity-log">
            {productionPlan.recent_activity.map((act, idx) => (
              <div key={idx} className="activity-entry" style={{ display: 'flex', gap: '12px', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#888', fontSize: '0.85em', whiteSpace: 'nowrap' }}>
                  {act.created_at ? new Date(act.created_at).toLocaleString() : ''}
                </span>
                <span>
                  <strong>{act.batch_number}</strong> — {act.formula_name} ({act.planned_weight.toLocaleString()} lbs) — {act.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Items by Delivery Date - GROUPED BY ORDER */}
      {productionPlan && (
        <div className="mill-section">
          <h2 className="mill-section-title">
            Orders by Delivery Date
          </h2>

          {dateGroups.length === 0 ? (
            <div className="mill-no-items">
              <p>No pending orders in the planning window.</p>
            </div>
          ) : (
            dateGroups.map((dateGroup, dateIdx) => (
              <div key={dateGroup.date} className="mill-date-group">
                <h3 className="mill-date-header">
                  {dateGroup.date === 'No Date' ? 'No Delivery Date' : formatDateLong(dateGroup.date)}
                  <span className="date-summary">
                    {dateGroup.total_orders} order{dateGroup.total_orders !== 1 ? 's' : ''}, {dateGroup.total_items} item{dateGroup.total_items !== 1 ? 's' : ''}
                  </span>
                </h3>

                <div className="orders-container">
                  {dateGroup.orders.map((order, orderIdx) => (
                    <div
                      key={order.order_id}
                      className={`order-card ${orderIdx % 2 === 0 ? 'order-card-even' : 'order-card-odd'}`}
                    >
                      {/* Order Header */}
                      <div className="order-card-header">
                        <div className="order-card-title">
                          <a
                            href={`/admin/orders/${order.order_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-number-link"
                          >
                            ORDER #{order.order_number}
                          </a>
                          {order.items.some(i => i.is_bridge_club) && <span className="bridge-club-badge">Bridge Club</span>}
                          {order.items.some(i => i.source === 'farmhand') && <span className="farmhand-badge">Farmhand</span>}
                          <span className="order-customer">{order.customer_name}</span>
                        </div>
                        <div className="order-card-meta">
                          <span className="order-meta-item">
                            {order.total_items} item{order.total_items !== 1 ? 's' : ''}
                          </span>
                          <span className="order-meta-divider">|</span>
                          <span className="order-meta-item order-total-weight">
                            {order.total_lbs.toLocaleString()} lbs total
                          </span>
                        </div>
                      </div>

                      {/* Order Items Table */}
                      <MobileScrollTable>
                      <table className="order-items-table">
                        <thead>
                          <tr>
                            <th>Formula</th>
                            <th>Form</th>
                            <th>Package</th>
                            <th>Quantity</th>
                            <th>Source</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map(item => {
                            const status = getItemStatus(item);
                            return (
                              <tr key={item.order_item_id}>
                                <td className="formula-cell">
                                  <strong>{item.formula_name}</strong>
                                </td>
                                <td>
                                  <span className={`feed-form-badge form-${item.feed_form.toLowerCase()}`}>
                                    {item.feed_form}
                                  </span>
                                </td>
                                <td className="package-cell">{getPackageDisplay(item)}</td>
                                <td className="quantity-cell">
                                  <strong>{item.total_quantity_lbs.toLocaleString()}</strong> lbs
                                  {item.number_of_packages > 0 && (
                                    <span className="package-count">
                                      ({item.number_of_packages} pkg)
                                    </span>
                                  )}
                                </td>
                                <td className="sources-cell">
                                  {renderSources(item)}
                                </td>
                                <td className="status-cell">
                                  <span className={`item-status ${status.className}`}>
                                    {status.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </MobileScrollTable>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Quick Navigation */}
      <div className="mill-quick-nav">
        <h3>Quick Access</h3>
        <div className="quick-nav-buttons">
          <button className="mill-btn mill-btn-secondary" onClick={() => navigate('/mill/packaging')}>
            Packaging Queue
          </button>
          <button className="mill-btn mill-btn-secondary" onClick={() => navigate('/mill/shipping')}>
            Shipping
          </button>
          <button className="mill-btn mill-btn-secondary" onClick={() => navigate('/mill/calendar')}>
            Production Calendar
          </button>
          <button className="mill-btn mill-btn-secondary" onClick={() => navigate('/mill/orders')}>
            Orders Overview
          </button>
          <button className="mill-btn mill-btn-secondary" onClick={() => navigate('/mill/recall')}>
            Recall Lookup
          </button>
        </div>
      </div>

      {showPlanModal && (
        <BatchPlanModal
          onClose={() => setShowPlanModal(false)}
          onStartMixing={(batchId) => { setShowPlanModal(false); navigate(`/mill/mixing/${batchId}`); }}
          onSaved={loadData}
        />
      )}
    </div>
  );
};

export default MillDashboard;
